import { BreadcrumbService } from 'src/app/angularJS/services/breadcrumbs/breadcrumb.service';
import { FileBuilderService } from 'src/app/angularJS/services/file-builder/file-builder.service';
import { FileExporterService } from 'src/app/angularJS/services/file-exporting/file-exporter.service';
import { JourneyUpdateService } from 'src/app/angularJS/services/journey-update/journey-update.service';
import { PdfGeneratorService } from 'src/app/angularJS/services/pdf-generator.service';
import { TransportLocationService } from 'src/app/angularJS/services/transport-location/transport-location.service';
import { TransportOperatorBusRouteService } from 'src/app/angularJS/services/transport-operator-route/transportOperatorRoute.service';

/**
 * A Controller used to setup the operator services binding with the associated operator services view
 */
export class OperatorServicesListController {
  public static $inject = [
    'transportOperatorBusRouteService',
    'operator',
    '$state',
    '$translate',
    'PrimaryAction',
    '$rootScope',
    '$stateParams',
    'pdfGeneratorService',
    'fileBuilderService',
    'fileExporterService',
    '$filter',
    'journeyUpdateService',
    'breadcrumbService',
    'transportLocationService'
  ];

  public transportOperatorService: TransportOperatorBusRouteService;
  public operatorServices: Array<InterACT.Interfaces.TransportOperatorServices.TransportOperatorService>;
  public exportFormats: Array<string>;
  private currentLocale: string;

  constructor(
    private transportOperatorBusRouteService: TransportOperatorBusRouteService,
    public operator: InterACT.Interfaces.TransportOperators.TransportOperator,
    private $state: ng.ui.IStateService,
    private $translate: ng.translate.ITranslateService,
    private PrimaryAction: any,
    private $rootScope,
    private $stateParams,
    private pdfGeneratorService: PdfGeneratorService,
    private fileBuilderService: FileBuilderService,
    private fileExporterService: FileExporterService,
    private $filter: ng.IFilterService,
    private journeyUpdateService: JourneyUpdateService,
    private breadcrumbService: BreadcrumbService,
    private transportLocationService: TransportLocationService
  ) {
    this.transportOperatorService = transportOperatorBusRouteService;
    this.exportFormats = [ 'PDF', 'CSV', 'XLSX' ];
    this.currentLocale = $translate.use();
    this.activate();
  }

  public async getLocationName(transportLocationLink: string) {

    if(transportLocationLink.includes("/transport-locations/")){
      const transportLocation = await this.transportLocationService.getTransportLocation(transportLocationLink.split('/').pop());
      
      return transportLocation.locationName;
    }
    
    return transportLocationLink;
  }

  /**
   * Function to retrieve records from the transport operators service Service
   *
   * @param page The number of the page of data to be retrieved
   * @param perPage The number of items per page to be retrieved
   *
   * @returns The response object returned from a httpPromise
   */
  public fetchCollection = (page?: number, perPage?: number) => {
    const qParam = 'operator.link=/transport-operators/' + this.$stateParams.operatorId;
    if (!angular.isNumber(page)) {
      page = 1;
    }
    if (!angular.isNumber(perPage)) {
      perPage = this.transportOperatorBusRouteService.perPageCount;
    }
    this.$rootScope.showSpinner = true;
    return this.transportOperatorBusRouteService.getList(page, perPage, qParam).then((result) => {
      result.forEach(async (transportOperatorService) => {
        if (transportOperatorService.startPoint) {
          transportOperatorService.startPoint = await this.getLocationName(transportOperatorService.startPoint);
        }

        if (transportOperatorService.endPoint) {
          transportOperatorService.endPoint = await this.getLocationName(transportOperatorService.endPoint);
          
        }
      });

      this.operatorServices = result;
      this.$rootScope.showSpinner = false;
    });
  }

  /**
   * Navigates to the service create view
   */
  public goToCreate = () => {
    this.$state.go('operator.services-create', { operator: this.operator });
  }

  /**
   * Navigates to the edit screen with the chosen operator service
   *
   * @param operatorServiceId The operatorServiceId of whom we want to edit
   * @param operatorId The operator id of whom we want to edit
   */
  public goToEdit = (operatorServiceId: string, operatorId: string) => {
    const operatorIdLink = operatorId.split('/').pop();
    this.$state.go('operator.services-edit',
      {
        operatorServiceId: operatorServiceId,
        operatorId: operatorIdLink,
        previousState: {
          name: 'operator.services',
          params: {
            operatorId: operatorIdLink
          }
        }
      }
    );
  }

  /**
   * Requests an exported version of the operator services
   *
   * @param format the export format
   */
  public exportTo = (format: string) => {
    const title = this.$translate.instant('OPERATOR_SERVICES');
    switch (format) {
      case 'CSV':
      case 'XLSX':
        this.$rootScope.showSpinner = true;
        this.fileBuilderService.buildExcelFile(
          '/transport-operator-services',
          this.constructQParam(),
          this.getOperatorServiceColumns(),
          this.currentLocale,
          format.toLowerCase(),
          title).then((response) => {
            this.$rootScope.showSpinner = false;
            this.fileExporterService.exportToExcel(response.data, format.toLowerCase(), title);
          });
        break;
      default:
        this.exportToPdf(title);
    }
  }

  /**
   * Exports the operator services list to pdf
   *
   * @param title the title of the pdf
   */
  public exportToPdf = (title: string) => {
    const dateFilter = this.$filter('date');
    this.transportOperatorBusRouteService.getList(undefined, undefined, this.constructQParam()).then((results) => {
      const columnWidths = [ '*', '*', '*', '*', '*', '*', '*' ];
      const headerRow = [
        this.$translate.instant('OPERATOR_SERVICE.REGISTRATION_NUMBER'),
        this.$translate.instant('OPERATOR_SERVICE.SERVICE_NUMBER'),
        this.$translate.instant('OPERATOR_SERVICE.ITSO_SERVICE'),
        this.$translate.instant('TIME_DETAIL.START_DATE'),
        this.$translate.instant('TIME_DETAIL.END_DATE'),
        this.$translate.instant('OPERATOR_SERVICE.START_POINT'),
        this.$translate.instant('OPERATOR_SERVICE.END_POINT')
      ];
      const sourceData = _.map(results, (operatorService) => {
        return [
          operatorService.registrationNumber,
          operatorService.serviceNumber,
          operatorService.alias ? operatorService.alias.join(', ') : '',
          dateFilter(operatorService.startDate, 'dd MMM yyyy'),
          operatorService.endDate ? dateFilter(operatorService.endDate, 'dd MMM yyyy') : '',
          operatorService.startPoint ? operatorService.startPoint : '',
          operatorService.endPoint ? operatorService.endPoint : ''
        ];
      });

      this.pdfGeneratorService.exportAsTable(headerRow, sourceData, columnWidths, title);
    });
  }

  public rematchOperatorServiceJourneys = (operatorServiceId: string) => {
    this.$rootScope.showSpinner = true;
    this.journeyUpdateService.rematchOperatorServiceJourneys({ operatorServiceId: operatorServiceId }).then(() => {
      this.$rootScope.showSpinner = false;
    });
  }

  /**
   * Returns a list of the operator service columns
   *
   * @returns the list of operator columns
   */
  private getOperatorServiceColumns = () => {
    return [
      { header: this.$translate.instant('OPERATOR_SERVICE.REGISTRATION_NUMBER'), key: 'regNumber' },
      { header: this.$translate.instant('OPERATOR_SERVICE.SERVICE_NUMBER'), key: 'serviceNumber' },
      { header: this.$translate.instant('OPERATOR_SERVICE.ITSO_SERVICE'), key: 'itsoService' },
      { header: this.$translate.instant('TIME_DETAIL.START_DATE'), key: 'startDate' },
      { header: this.$translate.instant('TIME_DETAIL.END_DATE'), key: 'endDate' },
      { header: this.$translate.instant('OPERATOR_SERVICE.START_POINT'), key: 'startPoint' },
      { header: this.$translate.instant('OPERATOR_SERVICE.END_POINT'), key: 'endPoint' }
    ];
  }
  private constructQParam = () => {
    return 'operator.link=/transport-operators/' + this.$stateParams.operatorId;
  }

  /**
   * Sets the primary action buttons
   */
  private activate() {
    this.fetchCollection(1, 15);

    this.PrimaryAction.setActions([ {
      name: 'Create Operator Service',
      action: this.goToCreate
    } ]);

    this.breadcrumbService.setBreadcrumbs([
      {
        name: 'BREADCRUMBS.REIMBURSEMENT.OPERATORS.OPERATORS',
        sref: 'operators'
      },
      {
        name: 'BREADCRUMBS.REIMBURSEMENT.OPERATORS.REGISTRATIONS',
        sref: 'operator.services'
      }
    ]);

  }
}